import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schema } from '@radiology/api-client';

/**
 * Purchasing data access.
 *
 * As in certifications, these talk straight to PostgREST and RLS decides what
 * comes back. An employee's query and an approver's query are the same SQL,
 * scoped differently by the database — so nothing here filters by hospital or
 * owner. If you find yourself adding such a filter for security rather than for
 * the UI, it belongs in a policy.
 *
 * The status filters below ARE for the UI: an approver is allowed to read their
 * hospital's rejected requests, the "Pending" screen just does not want them.
 */

export type RequestStatus = 'Pending' | 'Approved' | 'Rejected' | 'Deleted' | 'Chief approved';

/** Who raised the request. Carried over from the legacy rows as jsonb. */
export interface RequestCreator {
  id?: string;
  username?: string;
  hospital?: string;
  section?: string;
  title?: string;
}

export interface RequestComment {
  comment: string;
  authorName?: string;
  authorRole?: string;
  createdAt?: string;
}

export interface PurchaseRequest {
  request_id: string;
  creator: RequestCreator;
  serial_no: string;
  description: string;
  cat_no: string | null;
  existing_item: 'Yes' | 'No';
  unit_of_order: string | null;
  quantity: number;
  unit_price: number;
  total: number;
  justification: string;
  section_head_signature: string;
  deliver: unknown;
  assigned_to: string | null;
  department: string | null;
  status: RequestStatus;
  spr_number: string | null;
  created_at: string;
  updated_at: string | null;
  approved_at: string | null;
  chief_approved_at: string | null;
  rejected_at: string | null;
  comments: RequestComment[] | null;
  commits: unknown;
  creator_username: string;
  creator_hospital: string;
  creator_section: string;
}

const KEY = ['purchasing', 'requests'] as const;

/**
 * PostgREST caps a response at 1,000 rows and does not say that it truncated.
 * 443 requests fit today, but an admin is unscoped and this table only grows, so
 * it pages like the certificates query does.
 */
const PAGE = 1000;

export function useRequests(status?: RequestStatus | RequestStatus[]) {
  return useQuery({
    queryKey: [...KEY, status ?? 'all'],
    queryFn: async (): Promise<PurchaseRequest[]> => {
      const all: PurchaseRequest[] = [];

      for (let from = 0; ; from += PAGE) {
        let query = schema('purchasing')
          .from('purchase_requests')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, from + PAGE - 1);

        if (Array.isArray(status)) query = query.in('status', status);
        else if (status) query = query.eq('status', status);

        const { data, error } = await query.returns<PurchaseRequest[]>();
        if (error) throw new Error(error.message);

        all.push(...(data ?? []));
        if (!data || data.length < PAGE) break;
      }

      return all;
    },
  });
}

export interface RequestSummaryRow {
  hospital: string;
  section: string;
  status: RequestStatus;
  total: number;
  total_value: number;
  average_value: number;
}

/** Aggregates for the dashboards, so counting does not pull every row. */
export function useRequestSummary() {
  return useQuery({
    queryKey: ['purchasing', 'request_summary'],
    queryFn: async (): Promise<RequestSummaryRow[]> => {
      const { data, error } = await schema('purchasing')
        .from('request_summary')
        .select('*')
        .returns<RequestSummaryRow[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/**
 * Move a request along the approval chain.
 *
 * The rules are the trigger's, not this function's: which transitions are legal,
 * who may make them, that a rejection carries a comment, and the timestamps. A
 * failure here is the database talking, so its message is worth showing.
 */
export function useDecideRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: RequestStatus;
      comment?: string;
      existing?: RequestComment[] | null;
      reviewerName: string;
      reviewerRole: string;
    }) => {
      const patch: Record<string, unknown> = { status: input.status };

      // Comments are append-only history, not a single field. Any decision may
      // carry one; a rejection must.
      if (input.comment?.trim()) {
        patch.comments = [
          ...(input.existing ?? []),
          {
            comment: input.comment.trim(),
            authorName: input.reviewerName,
            authorRole: input.reviewerRole,
            createdAt: new Date().toISOString().slice(0, 10),
          },
        ];
      }

      const { error } = await schema('purchasing')
        .from('purchase_requests')
        .update(patch)
        .eq('request_id', input.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** A manager assigning the SPR number once a request is fully approved. */
export function useAssignSpr() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; sprNumber: string; assignedTo?: string }) => {
      const { error } = await schema('purchasing')
        .from('purchase_requests')
        .update({
          spr_number: input.sprNumber.trim(),
          ...(input.assignedTo ? { assigned_to: input.assignedTo.trim() } : {}),
        })
        .eq('request_id', input.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export interface NewRequestInput {
  serial_no: string;
  description: string;
  cat_no?: string;
  existing_item: 'Yes' | 'No';
  unit_of_order?: string;
  quantity: number;
  unit_price: number;
  justification: string;
  section_head_signature: string;
  department?: string;
}

/**
 * Raise a request.
 *
 * `creator` is assembled here from the session, but that is a convenience, not a
 * check: the INSERT policy requires `creator_username` to equal the caller's own
 * username and the status to be `Pending`, so a hand-crafted request naming
 * somebody else is rejected by the database.
 *
 * `total` is computed rather than sent by the caller — and `total` is not in the
 * authenticated UPDATE grant, so it cannot be edited afterwards either.
 */
export function useCreateRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: NewRequestInput & { creator: RequestCreator }) => {
      const { creator, quantity, unit_price, ...rest } = input;

      const { error } = await schema('purchasing')
        .from('purchase_requests')
        .insert({
          ...rest,
          creator,
          quantity,
          unit_price,
          total: Number((quantity * unit_price).toFixed(2)),
          status: 'Pending',
        });

      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export const STATUS_TONE: Record<
  RequestStatus,
  'success' | 'warning' | 'error' | 'info' | 'default'
> = {
  Pending: 'info',
  Approved: 'warning',
  'Chief approved': 'success',
  Rejected: 'error',
  Deleted: 'default',
};

/** Saudi riyals, the currency the legacy app priced everything in. */
export function money(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  return new Intl.NumberFormat('en-SA', {
    style: 'currency',
    currency: 'SAR',
    maximumFractionDigits: 2,
  }).format(n);
}
